import { observer } from 'mobx-react-lite'
import React, { useEffect, useReducer, useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import { TextInputWithLabel } from '../../components/Input'
import { ProfilePicture } from '../../components/ProfilePicture'
import Radom, { IOrganization } from '../../state/Radom'
import Web3 from '../../state/User'
import { RADOM_COLORS } from '../../util/Constants'
import Spinner from '../../components/Spinner'
import { isEqual, pickBy } from 'lodash'
import { errorToast, successToast } from '../../util/Util'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsEyebrow,
  SettingsGrid,
  SettingsHero,
  SettingsPage,

  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './SettingsLayout'

export const RADOM_TERMINAL_ENABLED_KEY = 'RADOM_TERMINAL_ENABLED'

const NameInput = styled.input<{ $isEditing: boolean }>`
  background: transparent;
  width: 100%;
  border: none;
  box-shadow: none;
  color: #20242d;
  font-size: 24px;
  font-weight: 500;
  border-radius: 0;
  padding: 0 0 4px;
  outline: none;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid ${({ $isEditing }) => $isEditing ? RADOM_COLORS.GRAY_DARK : 'transparent'};

  :focus {
    border-color: ${RADOM_COLORS.ORANGE};
  }
`

const ProfileSummary = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr);
  min-width: 0;
`

const ProfileIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const ProfileActions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 2px;
`

const Profile = observer(() => {
  const emptySellerProfile: any = {
    organizationId: '',
    name: '',
    logoUrl: '',
    website: '',
    telegram: '',
    discord: '',
    twitter: '',
    imageBytes: undefined
  }

  const [profile, updateProfile] = useReducer((prev, next) => {
    if (next.__replace) {
      const { __replace, ...replacementProfile } = next
      const parsedReplacement = Object.fromEntries(Object.entries(replacementProfile).filter(([_, v]) => v != null))
      return { ...emptySellerProfile, ...parsedReplacement }
    }

    const parsedNext = Object.fromEntries(Object.entries(next).filter(([_, v]) => v != null))
    return { ...prev, ...parsedNext }
  }, emptySellerProfile)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [profileSnapshot, setProfileSnapshot] = useState<IOrganization>(profile)
  const [profileChanges, setProfileChanges] = useState<Partial<IOrganization>>({})
  const [imgUrl, setImgUrl] = useState<string | undefined>()
  const [imgFile, setImgFile] = useState<File | undefined>()

  const onUpdateProfile = async (): Promise<void> => {
    if (isUpdating) return
    setIsUpdating(true)
    try {
      await Radom.updateSellerProfile({ organizationId: profile.organizationId, ...profileChanges })
      Radom.getSellerProfile()
      setProfileSnapshot(profile)
      successToast('Successfully updated seller profile')
    } catch (err) {
      console.error('Failed to create seller profile', err)
      errorToast(err.response?.data?.error || err.message)
    }
    setIsUpdating(false)
  }

  const onCancelEdit = (): void => {
    updateProfile({ __replace: true, ...profileSnapshot })
    setImgFile(undefined)
    setImgUrl(profileSnapshot.logoUrl)
  }

  useEffect(() => {
    Web3.connectedAccount
      .then(() => {
        Radom.getSellerProfile()
          .then(p => {
            updateProfile({ __replace: true, ...p })
            setProfileSnapshot(p)
            if (p.logoUrl) {
              setImgUrl(p.logoUrl)
            }
          })
          .catch(_ => {
            console.error('Failed')
            const emptyProfile = {
              sellerAddress: Web3.accounts[0]
            }
            updateProfile({ __replace: true, ...emptyProfile })
            setProfileSnapshot({ ...profile, ...emptyProfile })
          })
          .finally(() => setIsLoading(false))
      })
  }, [])

  useEffect(() => {
    const changes = pickBy(profile, (v, k) => {
      return !isEqual(profileSnapshot[k], v)
    })
    setProfileChanges({ ...changes })
  }, [profile, profileSnapshot])

  useEffect(() => {
    if (imgFile) {
      setImgUrl(URL.createObjectURL(imgFile))
      imgFile.arrayBuffer()
        .then(b => {
          updateProfile({
            imageBytes: Array.from(new Uint8Array(b))
          })
        })
    }
  }, [imgFile])

  if (isLoading) {
    return <Spinner />
  }

  const hasProfileChanges = Object.keys(profileChanges).length > 0
  const isProfileComplete = Boolean(profile.name && profile.website && (profile.logoUrl || imgUrl || imgFile))

  return <SettingsPage>
    <SettingsHero>
      <div>
        <SettingsEyebrow>Organization settings</SettingsEyebrow>
        <SettingsTitle>Branding</SettingsTitle>
        <SettingsSubtext>
          Manage the seller profile shown across hosted checkout, invoices, and payment links.
        </SettingsSubtext>
      </div>
    </SettingsHero>

    <SettingsCard>
      <SettingsCardHeader>
        <ProfileSummary>
          <ProfilePicture
            imgUrl={imgUrl}
            imgFile={imgFile}
            setImgFile={setImgFile}
            isEditing
          />
          <ProfileIdentity>
            <SettingsCardTitle>Seller profile</SettingsCardTitle>
            <NameInput
              value={profile.name}
              placeholder={profile.sellerAddress || 'Merchant name'}
              onChange={(e) => updateProfile({ name: (e.target as HTMLInputElement).value })}
              $isEditing
            />
            <SettingsCardDescription>
              This identity appears on contact-facing payment pages and receipts.
            </SettingsCardDescription>
          </ProfileIdentity>
        </ProfileSummary>
        <StatusPill $tone={isProfileComplete ? 'green' : 'amber'}>
          {isProfileComplete ? 'Complete' : 'Needs attention'}
        </StatusPill>
      </SettingsCardHeader>

      <SettingsCardBody>
        <SettingsGrid>
          <TextInputWithLabel
            label="Website URL"
            value={profile.website}
            placeholder="https://radom.com/"
            onChange={(e) => updateProfile({ website: (e.target as HTMLInputElement).value })}
          />
          <TextInputWithLabel
            label="Telegram"
            value={profile.telegram}
            placeholder="@radomnetwork"
            onChange={(e) => updateProfile({ telegram: (e.target as HTMLInputElement).value })}
          />
          <TextInputWithLabel
            label="Discord"
            value={profile.discord}
            placeholder="https://discord.gg/invite/aX3Rtv"
            onChange={(e) => updateProfile({ discord: (e.target as HTMLInputElement).value })}
          />
          <TextInputWithLabel
            label="X (Formerly Twitter)"
            value={profile.twitter}
            placeholder="@radomnetwork"
            onChange={(e) => updateProfile({ twitter: (e.target as HTMLInputElement).value })}
          />
        </SettingsGrid>
      </SettingsCardBody>
    </SettingsCard>

    {hasProfileChanges && (
      <ProfileActions>
        <SecondaryButton
          style={{ color: RADOM_COLORS.GRAY_DARKER }}
          onClick={onCancelEdit}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          isLoading={isUpdating}
          style={{ minWidth: 220 }}
          disabled={isUpdating}
          onClick={onUpdateProfile}>
          Save changes
        </PrimaryButton>
      </ProfileActions>
    )}
  </SettingsPage>
})

export default Profile
